import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { LoginAccount, Referral, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import { ITEMS_PER_PAGE } from "utils/constant";
import PageHeader from "components/PageHeader";
import {
  fetchAssetReferralByProsecutorId,
  fetchAssetReferralsByUnit,
  fetchReferralsByAdminUnit,
} from "services/assetRecoveryService";

// Role-based tabs
const registrarTabs = ["All", "New Request", "Registered", "Archived"];
const prosecutorTabs = ["All", "New Request", "On litigation", "Completed"];
const directorTabs = [
  "All",
  "New Request",
  "Assigned",
  "On Litigation",
  "Closed",
  "Terminated",
];

// Status mapping for tabs
const statusMappings: Record<string, string[]> = {
  // Registrar tabs
  "New Request": ["Pending", "Forwarded to Registrar"],
  Registered: [
    "Prohibition Request Approved",
    "Asset Recovery Request Approved",
    "Court Case Filed",
  ],
  Archived: [
    "Recovered",
    "Closed by cordinator",
    "Closed by prosecutor",
    "Terminated By Prosecutor",
  ],

  // Prosecutor tabs
  "On litigation": [
    "Prohibition On litigation",
    "Asset Recovery On Litigation",
    "Court Case Filed",
  ],
  Completed: [
    "Recovered",
    "Closed by prosecutor",
    "Asset Recovery Final Verdict",
  ],

  // Director tabs
  Assigned: ["Assigned to Prosecutor", "Forwarded to Coordinator"],
  "On Litigation": [
    "Prohibition On litigation",
    "Asset Recovery On Litigation",
    "Court Case Filed",
  ],
  Closed: ["Recovered", "Closed by cordinator", "Closed by prosecutor"],
  Terminated: ["Terminated By Prosecutor", "Suspended"],
};

const statusOptions = [
  "Pending",
  "Forwarded to Lower",
  "Forwarded to Higher",
  "Investigation Ongoing",
  "Prohibition Requested",
  "Prohibition Request Approved",
  "Prohibition On Revision",
  "Prohibition On Updated",
  "Prohibition On litigation",
  "Prohibition Final Verdict",
  "Asset Recovery Requested",
  "Asset Recovery Request Approved",
  "Asset Recovery On Revision",
  "Asset Recovery Updated",
  "Asset Recovery On Litigation",
  "Asset Recovery Final Verdict",
  "Court Case Filed",
  "Execution in Progress",
  "Recovered",
  "Closed by cordinator",
  "Closed by prosecutor",
  "Termination Requested",
  "Termination Rejected",
  "Terminated By Prosecutor",
  "Suspension Requested",
  "Suspended",
  "Suspension Rejected",
  "Reinitiate Requested",
  "Reinitiate Rejected",
  "Reinitiate Aproved",
] as const;

type StatusType = (typeof statusOptions)[number];

const AssetReferal: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [filteredReferrals, setFilteredReferrals] = useState<Referral[]>([]);
  const navigate = useNavigate();

  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isDirector = hasRole(loginData?.roles, [Roles.director]);

  // Get role-specific tabs
  const getRoleTabs = () => {
    if (isAssetRegistrar) return registrarTabs;
    if (isAssetProsecutor) return prosecutorTabs;
    if (isAssetCoordinator) return directorTabs;
    if (isDirector) return directorTabs;
    return ["All"]; // Default fallback
  };

  const roleTabs = getRoleTabs();

  // Filter states
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<StatusType | "">("");

  useEffect(() => {
    setLoading(true);
    if (isAssetProsecutor) {
      fetchAssetReferralByProsecutorId(loginData as LoginAccount)
        .then((data) => {
          setReferrals(data);
          setFilteredReferrals(data);
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(errorHandler(error));
          setLoading(false);
        });
    } else if (isAssetRegistrar) {
      fetchReferralsByAdminUnit(loginData?.admin_unit.toString() as string)
        .then((data) => {
          setReferrals(data);
          setFilteredReferrals(data);
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(errorHandler(error));
          setLoading(false);
        });
    } else {
      fetchAssetReferralsByUnit(
        loginData?.admin_unit.toString() as string,
        loginData?.org_unit.toString() as string,
      )
        .then((data) => {
          setReferrals(data);
          setFilteredReferrals(data);
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(errorHandler(error));
          setLoading(false);
        });
    }
  }, [
    isAssetProsecutor,
    isAssetRegistrar,
    loginData?.admin_unit,
    loginData?.org_unit,
  ]);

  // Apply filters based on active tab and other filters
  useEffect(() => {
    let result = referrals;

    // Apply tab filter
    if (activeTab !== "All") {
      const statusesForTab =
        statusMappings[activeTab as keyof typeof statusMappings];
      if (statusesForTab) {
        result = result.filter((referral) =>
          statusesForTab.includes(referral?.action_status as string),
        );
      }
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (referral) =>
          referral?.referral_id?.toLowerCase().includes(searchLower) ||
          referral?.registerd_by?.toLowerCase().includes(searchLower) ||
          referral?.action_status?.toLowerCase().includes(searchLower) ||
          referral?.receiving_office?.toLowerCase().includes(searchLower) ||
          referral?.letter_number?.toLowerCase().includes(searchLower),
      );
    }

    // Apply date filter
    if (selectedDate) {
      result = result.filter((referral) => {
        if (!referral?.date) return false;
        const referralDate = new Date(referral.date)
          .toISOString()
          .split("T")[0];
        return referralDate === selectedDate;
      });
    }

    // Apply status filter
    if (selectedStatus) {
      result = result.filter(
        (referral) => referral?.action_status === selectedStatus,
      );
    }

    setFilteredReferrals(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [activeTab, search, selectedDate, selectedStatus, referrals]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedReferrals = filteredReferrals.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredReferrals.length / ITEMS_PER_PAGE);

  const handleDateFilter = (date: string) => {
    setSelectedDate(date);
  };

  const clearAllFilters = () => {
    setSearch("");
    setSelectedDate("");
    setSelectedStatus("");
    setActiveTab("All");
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    // Clear other filters when switching tabs for better UX
    setSelectedStatus("");
  };

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Asset Referral List"
        icon={FaBriefcase}
        tabs={roleTabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
        rightContent={
          <>
            {isAssetCoordinator && (
              <button
                onClick={() => navigate("/asset-register")}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                Register Asset Referral
              </button>
            )}
            {(isAssetProsecutor || isAssetRegistrar || isDirector) && (
              <div className="flex items-end gap-4">
                {/* Date Filter */}
                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-600 mb-1">
                    Filter by Date
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="px-3 py-2 text-sm border rounded w-48"
                      value={selectedDate}
                      onChange={(e) => handleDateFilter(e.target.value)}
                    />
                    {selectedDate && (
                      <button
                        onClick={() => setSelectedDate("")}
                        className="px-2 py-1 text-xs text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                        title="Clear date filter"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        }
      />

      {/* Filter Summary */}
      {(activeTab !== "All" || search || selectedDate || selectedStatus) && (
        <div className="p-3 text-sm bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span>
              Showing {filteredReferrals.length} of {referrals.length} referrals
              {activeTab !== "All" && ` in "${activeTab}"`}
              {search && ` matching "${search}"`}
              {selectedDate && ` on ${selectedDate}`}
              {selectedStatus && ` with status "${selectedStatus}"`}
            </span>
            <button
              onClick={clearAllFilters}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden bg-white shadow rounded-xl">
        {loading ? (
          <Loading label="Loading Referrals" />
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="text-white bg-black">
              <tr>
                <th colSpan={7} className="p-4 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <button
                        onClick={() => navigate(-1)}
                        className="text-white hover:text-gray-300"
                        title="Go Back"
                      >
                        Back
                      </button>
                      <span>List Table</span>
                    </div>

                    <div className="relative w-80">
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search referrals..."
                        className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                      />
                    </div>
                  </div>
                </th>
              </tr>
              <tr>
                <th className="p-3 text-left">Asset Ref. No</th>
                <th className="p-3 text-left">Registered By</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Reception Office</th>
                {/* <th className="p-3 text-left">Letter Office Number</th> */}
                <th className="p-3 text-left rounded-tr-lg">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReferrals.map((referral, idx) => (
                <tr
                  key={idx + indexOfFirstItem}
                  className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                >
                  <td className="p-3">{referral?.referral_id}</td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    {referral?.registerd_by || "N/A"}
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    <p className="bg-gray-100 rounded text-gray-800 p-2 w-fit">
                      {referral?.action_status}
                    </p>
                  </td>
                  <td className="p-3 font-[500] font-[Poppins]">
                    {referral?.receiving_office}
                  </td>
                  {/* <td className="p-3 font-[500] font-[Poppins]">
                    {referral?.letter_number}
                  </td> */}
                  <td className="p-3 font-[500] font-[Poppins]">
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/asset-recovery/${referral.id}`);
                      }}
                      className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {filteredReferrals.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    {referrals.length === 0
                      ? "No referrals found."
                      : "No referrals match your search criteria."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AssetReferal;
